import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import EmailLayout from '../../../layouts/EmailLayout';
import InboxToolbar from '../../../components/modules/email/InboxToolbar';
import EmailRow from '../../../components/modules/email/EmailRow';
import EmailDetail from './EmailDetail';
import EmailSidebar from '../../../components/modules/email/EmailSidebar';
import BulkSelectProvider from '../../../providers/BulkSelectProvider';
import { Col, Spinner, Alert, Button as BsButton, Row } from 'react-bootstrap';
import { Email } from '../../../data/email';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faInbox, 
  faColumns, 
  faExpand, 
  faSync,
  faExclamationTriangle,
  faChevronDown,
  faFolder,
  faPaperPlane,
  faEdit,
  faTrash,
  faArchive,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { getEmailConfig } from '../../../service/emailReadService';

const Inbox = () => {
  const { folderPath } = useParams<{ folderPath?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedEmailId, setSelectedEmailId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'split'>('list');
  
  // 🔧 INTENTAR USAR EL STORE SI ESTÁ DISPONIBLE, SINO FALLBACK A ESTADO LOCAL
  const [localEmails, setLocalEmails] = useState<Email[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  
  // Intentar obtener el store
  const getStore = () => {
    if (typeof window !== 'undefined' && window.getEmailsStore) {
      return window.getEmailsStore();
    }
    return null;
  };
  
  const store = getStore();
  const hasStore = !!store;
  
  // Si hay store, usar su estado, sino usar estado local
  const emails = hasStore ? store.getState().emails : localEmails;
  const loading = hasStore ? store.getState().loading : localLoading;
  const error = hasStore ? store.getState().error : localError;
  
  // 🔧 CORRECCIÓN: Effect para manejar la navegación cuando volvemos de ver un email
  useEffect(() => {
    const navigationState = location.state as any;
    if (navigationState?.folderPath && !folderPath && location.pathname === '/anubis/email/inbox') {
      // Si venimos de ver un email y la carpeta no era INBOX, navegar a la carpeta correcta
      if (navigationState.folderPath !== 'INBOX') {
        console.log(`📁 Restoring folder from navigation state: ${navigationState.folderPath}`);
        navigate(`/anubis/email/folder/${encodeURIComponent(navigationState.folderPath)}`, { 
          replace: true,
          state: null // Limpiar el state para evitar loops
        });
      }
    }
  }, [location.state, folderPath, navigate]);

  // 🔧 CORRECCIÓN: Determinar la carpeta actual correctamente
  const getCurrentFolder = () => {
    // 1. Si hay state de navegación (volviendo de email-detail)
    const navigationState = location.state as any;
    if (navigationState?.folderPath && !folderPath && location.pathname === '/anubis/email/inbox') {
      // Estamos volviendo de ver un email, usar la carpeta del state
      return navigationState.folderPath;
    }
    
    // 2. Si es la ruta de inbox explícita
    if (location.pathname === '/anubis/email/inbox') {
      return 'INBOX';
    }
    
    // 3. Si hay folderPath en la URL, usarlo
    if (folderPath) {
      return decodeURIComponent(folderPath);
    }
    
    // 4. Si no hay ruta específica y tenemos store, intentar usar la carpeta guardada
    if (hasStore && !location.pathname.includes('/email/inbox')) {
      const savedFolder = store.getCurrentFolder();
      if (savedFolder) {
        return savedFolder;
      }
    }
    
    // 5. Default a INBOX
    return 'INBOX';
  };

  const selectedFolder = getCurrentFolder();

  // 🔧 FUNCIÓN: Obtener icono y nombre de carpeta
  const getFolderIcon = (folder: string) => {
    const folderLower = folder.toLowerCase();
    if (folderLower === 'inbox') return faInbox;
    if (folderLower === 'sent' || folderLower === 'enviados') return faPaperPlane;
    if (folderLower === 'drafts' || folderLower === 'borradores') return faEdit;
    if (folderLower === 'trash' || folderLower === 'papelera') return faTrash;
    if (folderLower === 'spam' || folderLower === 'junk') return faExclamationCircle;
    if (folderLower === 'archive' || folderLower === 'archivo') return faArchive;
    return faFolder;
  };

  const getFolderDisplayName = (folder: string) => {
    const folderLower = folder.toLowerCase();
    if (folderLower === 'inbox') return 'Bandeja de entrada';
    if (folderLower === 'sent') return 'Enviados';
    if (folderLower === 'drafts') return 'Borradores';
    if (folderLower === 'trash') return 'Papelera';
    if (folderLower === 'spam' || folderLower === 'junk') return 'Spam';
    if (folderLower === 'archive') return 'Archivo';
    
    // Para carpetas personalizadas, mostrar el último segmento
    const parts = folder.split('/');
    return parts[parts.length - 1] || folder;
  };

  // Cargar emails cuando cambie la carpeta
  const loadEmails = async (folder: string) => {
    console.log(`📧 Loading emails for folder: ${folder}`);
    
    // 🔧 SI HAY STORE, USAR EL STORE
    if (hasStore) {
      console.log('📦 Using EmailsStore');
      store.loadEmails(folder, 1, true);
      return;
    }
    
    // 🔧 FALLBACK: MÉTODO ORIGINAL SI NO HAY STORE
    console.log('📧 Using local state (no store)');
    const config = getEmailConfig();
    
    if (!config) {
      setLocalError('No hay configuración de email. Configure su cuenta primero.');
      setLocalEmails([]);
      return;
    }

    setLocalLoading(true);
    setLocalError(null);
    
    try {
      const response = await fetch('https://backend.anubisai.net/api/pmail/get-emails', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({
          server: config.server,
          username: config.username,
          password: config.password,
          imapPort: config.imapPort,
          ssl: config.ssl,
          folderPath: folder,
          limit: 50,
          offset: 0
        })
      });

      const result = await response.json();
      console.log(`📧 Response for ${folder}:`, result);

      if (response.ok && result.success) {
        // Convertir emails del servidor al formato esperado por EmailRow
        const formattedEmails: Email[] = result.emails.map((email: any) => ({
          id: email.id,
          sender: {
            name: email.sender.name,
            avatar: email.sender.avatar || undefined
          },
          subject: email.subject,
          details: email.details,
          time: email.time,
          starred: email.starred,
          read_at: email.read_at,
          attachments: email.attachments
        }));
        
        console.log(`📧 Loaded ${formattedEmails.length} emails for ${folder}`);
        setLocalEmails(formattedEmails);
        setLocalError(null);
        setLastUpdate(new Date());
      } else {
        setLocalError(result.error || 'Error al cargar emails');
        setLocalEmails([]);
      }
    } catch (err) {
      console.error('Error loading emails:', err);
      setLocalError('Error de conexión al cargar emails');
      setLocalEmails([]);
    } finally {
      setLocalLoading(false);
    }
  };

  // Cargar emails cuando cambie la carpeta
  useEffect(() => {
    console.log(`📂 Folder changed to: ${selectedFolder}`);
    loadEmails(selectedFolder);
  }, [selectedFolder]);

  // 🔧 SUSCRIBIRSE AL STORE SI ESTÁ DISPONIBLE
  useEffect(() => {
    if (hasStore) {
      console.log('📦 Subscribing to EmailsStore');
      const unsubscribe = store.subscribe(() => {
        // Forzar re-render cuando cambie el store
        setLocalEmails([]); // Trigger para que React detecte el cambio
      });
      
      return unsubscribe;
    }
  }, [hasStore, store]);

  const handleEmailSelect = (emailId: number) => {
    setSelectedEmailId(emailId);
    // No navegar si estamos en modo split
    if (viewMode === 'list') {
      navigate('/anubis/email/email-detail', {
        state: { emailId, folderPath: selectedFolder }
      });
    }
  };

  const toggleViewMode = () => {
    const newMode = viewMode === 'list' ? 'split' : 'list';
    setViewMode(newMode);
    if (newMode === 'list') {
      setSelectedEmailId(null);
    }
  };

  const handleRefresh = () => {
    console.log('🔄 Manual refresh triggered');
    
    if (hasStore) {
      console.log('📦 Refreshing via EmailsStore');
      store.refreshEmails(true);
    } else {
      console.log('📧 Refreshing via local method');
      loadEmails(selectedFolder);
    }
  };

  // Función para interceptar clicks en EmailRow
  const handleRowClick = (e: React.MouseEvent, emailId: number) => {
    e.preventDefault();
    e.stopPropagation();
    handleEmailSelect(emailId);
  };

  // En modo split, renderizar con 3 columnas
  if (viewMode === 'split') {
    return (
      <div className="email-container">
        <Row className="g-lg-4 g-xl-6 mb-8">
          {/* Sidebar - columna 1 */}
          <Col lg="auto">
            <div 
              className="email-sidebar email-sidebar-width bg-body d-none d-lg-block"
              style={{ height: 'calc(100vh - 100px)', overflowY: 'auto' }}
            >
              <EmailSidebar />
            </div>
          </Col>

          {/* Lista de emails - columna 2 */}
          <Col xs={12} md={4} lg={3}>
            <div 
              className="email-content scrollbar"
              style={{ height: 'calc(100vh - 100px)', overflowY: 'auto' }}
            >
              <div className="px-lg-1">
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between mb-3 px-2">
                  <div>
                    <h5 className="mb-0 d-flex align-items-center">
                      <FontAwesomeIcon 
                        icon={getFolderIcon(selectedFolder)} 
                        className="me-2 text-primary" 
                        size="sm" 
                      />
                      {getFolderDisplayName(selectedFolder)}
                    </h5>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {!loading && emails.length > 0 && (
                      <span className="text-body-tertiary fs-9">
                        {emails.length} mensajes
                      </span>
                    )}
                    <BsButton
                      variant="outline-primary"
                      size="sm"
                      onClick={handleRefresh}
                      disabled={loading}
                      title="Actualizar correos"
                    >
                      <FontAwesomeIcon 
                        icon={faSync} 
                        className={loading ? 'fa-spin' : ''} 
                      />
                    </BsButton>
                    <BsButton
                      variant="outline-secondary"
                      size="sm"
                      onClick={toggleViewMode}
                      title="Vista de lista"
                      style={{ minWidth: '40px' }}
                      disabled={true}
                    >
                      <FontAwesomeIcon icon={faExpand} />
                    </BsButton>
                  </div>
                </div>

                <BulkSelectProvider data={emails}>
                  <InboxToolbar 
                    size="sm"
                    emails={emails}
                    loading={loading}
                    totalCount={emails.length}
                    currentPage={1}
                    itemsPerPage={50}
                    lastUpdate={lastUpdate}
                    onRefresh={handleRefresh}
                  />
                  
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" size="sm" />
                      <p className="mt-3 text-body-tertiary">
                        Cargando emails de {getFolderDisplayName(selectedFolder)}...
                      </p>
                    </div>
                  ) : error ? (
                    <Alert variant="danger" className="d-flex align-items-center mx-3 mt-3">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                      <div className="flex-1">
                        <strong>Error al cargar emails:</strong>
                        <br />
                        {error}
                      </div>
                      <BsButton variant="outline-danger" size="sm" onClick={handleRefresh}>
                        Reintentar
                      </BsButton>
                    </Alert>
                  ) : emails.length === 0 ? (
                    <div className="text-center py-5">
                      <FontAwesomeIcon 
                        icon={getFolderIcon(selectedFolder)} 
                        size="3x" 
                        className="text-muted mb-3" 
                      />
                      <p className="text-muted">No hay emails en {getFolderDisplayName(selectedFolder)}</p>
                    </div>
                  ) : (
                    <div className="email-list">
                      {emails.map((email, index) => (
                        <div 
                          key={email.id}
                          onClick={(e) => handleRowClick(e, email.id)}
                          className={`email-row-wrapper ${selectedEmailId === email.id ? 'bg-light' : ''}`}
                          style={{ cursor: 'pointer' }}
                        >
                          <EmailRow 
                            email={email} 
                            index={index} 
                            folderPath={selectedFolder}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </BulkSelectProvider>
              </div>
            </div>
          </Col>

          {/* Detalle del email - columna 3 */}
          <Col xs={12} md={8} lg className="flex-1">
            <div style={{ height: 'calc(100vh - 100px)', overflowY: 'auto' }}>
              {selectedEmailId ? (
                <EmailDetail 
                  emailId={selectedEmailId} 
                  folderPath={selectedFolder}
                  embedded={true}
                />
              ) : (
                <div className="email-content h-100 d-flex align-items-center justify-content-center">
                  <div className="text-center text-muted">
                    <FontAwesomeIcon 
                      icon={getFolderIcon(selectedFolder)} 
                      size="3x" 
                      className="mb-3" 
                    />
                    <p>Selecciona un email para ver su contenido</p>
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div> 
    );
  }

  // Vista normal (lista completa)
  return (
    <EmailLayout page="inbox">
      <Col>
        <div className="email-content scrollbar">
          <div className="px-lg-1">
            {/* Header con el nombre de la carpeta y botón de vista */}
            <div className="d-flex align-items-center justify-content-between mb-3 px-3">
              <div>
                <h5 className="mb-0 d-flex align-items-center">
                  <FontAwesomeIcon 
                    icon={getFolderIcon(selectedFolder)} 
                    className="me-2 text-primary" 
                    size="sm" 
                  />
                  {getFolderDisplayName(selectedFolder)}
                </h5>
              </div>
              <div className="d-flex align-items-center gap-2">
                {!loading && emails.length > 0 && (
                  <span className="text-body-tertiary fs-9">
                    {emails.length} {emails.length === 1 ? 'mensaje' : 'mensajes'}
                  </span>
                )}
                
                <BsButton
                  variant="outline-primary"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={loading}
                  title="Actualizar correos"
                >
                  <FontAwesomeIcon 
                    icon={faSync} 
                    className={loading ? 'fa-spin' : ''} 
                  />
                </BsButton>
                
                <BsButton
                  variant="outline-secondary"
                  size="sm"
                  onClick={toggleViewMode}
                  title="Vista dividida"
                  style={{ minWidth: '40px' }}
                  disabled={true}
                >
                  <FontAwesomeIcon icon={faColumns} />
                </BsButton>
              </div>
            </div>

            <BulkSelectProvider data={emails}>
              <InboxToolbar 
                emails={emails}
                loading={loading}
                totalCount={emails.length}
                currentPage={1}
                itemsPerPage={50}
                lastUpdate={lastUpdate}
                onRefresh={handleRefresh}
              />
              
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-body-tertiary">
                    Cargando emails de {getFolderDisplayName(selectedFolder)}...
                  </p>
                </div>
              ) : error ? (
                <Alert variant="danger" className="d-flex align-items-center mx-3 mt-3">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  <div className="flex-1">
                    <strong>Error al cargar emails:</strong>
                    <br />
                    {error}
                  </div>
                  <BsButton variant="outline-danger" size="sm" onClick={handleRefresh}>
                    Reintentar
                  </BsButton>
                </Alert>
              ) : emails.length === 0 ? (
                <div className="text-center py-5">
                  <FontAwesomeIcon 
                    icon={getFolderIcon(selectedFolder)} 
                    className="fa-3x text-body-tertiary mb-3" 
                  />
                  <p className="text-body-tertiary">
                    No hay emails en {getFolderDisplayName(selectedFolder)}
                  </p>
                </div>
              ) : (
                emails.map((email, index) => (
                  <div 
                    key={email.id}
                    onClick={() => handleEmailSelect(email.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <EmailRow 
                      email={email} 
                      index={index} 
                      folderPath={selectedFolder}
                    />
                  </div>
                ))
              )}
            </BulkSelectProvider>
          </div>
        </div>
      </Col>
    </EmailLayout>
  );
};

export default Inbox;